import React, { FC } from 'react';

import { styled } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { BaseAnswer } from '~/blocks/TestCard/TestCard';
import StatusDescription from '~/components/Test/CarouselQuestion/StatusDescription';
import { RadioQuestionProps } from '~/components/Test/types';
import { BodyText } from '~/components/UI/Texts';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '~/theme';

interface CarouselQuestionProps extends Omit<RadioQuestionProps, 'colorsMode'> {}

const CarouselQuestion: FC<CarouselQuestionProps> = ({
  answers,
  onSelect,
  submitted,
  isCorrect,
  isCorrectByIdMap,
  selectedAnswersIds,
}) => {
  const swiperSettings: SwiperOptions = {
    pagination: {
      clickable: true,
    },
    navigation: true,
  };

  return (
    <div>
      <StyledSwiper
        {...swiperSettings}
        onAfterInit={() => {
          onSelect(answers[0]?.id);
        }}
        onSlideChange={(swiper) => {
          onSelect(answers[swiper.activeIndex]?.id);
        }}
        data-testid="swiper"
      >
        {answers.map((answer) => {
          const currentId = answer?.id || '';
          const isCurrentIdCorrect = isCorrectByIdMap[currentId];
          const isSelected = selectedAnswersIds.includes(currentId);

          return (
            <SwiperSlide key={answer?.id}>
              <Answer
                as="div"
                data-testid="quizQuestionAnswer"
                selected={isSelected}
                passed={submitted && isCurrentIdCorrect}
                failed={submitted && isSelected && !isCurrentIdCorrect}
                finished={submitted}
              >
                <AnswerContent>{answer?.title}</AnswerContent>
              </Answer>
            </SwiperSlide>
          );
        })}
      </StyledSwiper>
      {submitted && <StyledStatusDescription isCorrect={isCorrect} />}
    </div>
  );
};

export default CarouselQuestion;

const StyledSwiper = styled(Swiper)`
  && .swiper-button-disabled::after {
    color: #fff;
  }

  .swiper-button-prev,
  .swiper-button-next {
    display: flex;
    opacity: 1;
    width: auto;
    height: auto;
    padding: 8px;
    top: 49%;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    .swiper-button-prev,
    .swiper-button-next {
      top: 39%;
    }
  }

  .swiper-button-prev::after,
  .swiper-button-next::after {
    color: #fff;
    width: 24px;
    height: 24px;
    font-size: 17px;
    line-height: inherit;
    text-align: center;
    font-weight: 900;
  }

  .swiper-pagination {
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  .swiper-pagination-bullet,
  .swiper-pagination-bullet-active {
    width: 16px;
    height: 16px;
    margin: 0;
  }

  .swiper-pagination-bullet {
    border-color: ${({ theme }) =>
      theme.isSsku && theme.palette.mode === 'light' && theme.palette.secondary.contrastText};
  }

  .swiper-pagination-bullet-active {
    background-color: ${({ theme }) => theme.palette.primary.main};
    border-color: ${({ theme }) =>
      theme.isSsku && theme.palette.mode === 'light' && theme.palette.primary.main};
  }
`;
const AnswerContent = styled(BodyText)`
  width: 100%;
  text-align: center;
`;
const StyledStatusDescription = styled(StatusDescription)`
  margin-top: ${SMALL_MARGIN_PX};
`;
const Answer = styled(BaseAnswer)`
  padding: ${SMALL_MARGIN_PX};
  margin-inline: ${MIDDLE_MARGIN_PX};
  border-radius: 4px;
  transition-delay: ${({ finished }) => !finished && '0.4s'};
`;
